import { inject, Injectable } from '@angular/core';
import {
  collection,
  doc,
  deleteDoc,
  DocumentReference,
  Firestore,
  getDocs,
  Query,
  query,
  setDoc,
  where
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { PlanningLateAck } from '../models/planning-late-ack.model';
import { PlanningCapacity } from '../models/planning-capacity.model';
import {
  PlanningMachineEvent,
  PlanningMachineEventType
} from '../models/planning-machine-event.model';
import { PlanningOperation } from '../models/planning-operation.model';

export type PlanningMachineEventPayload = {
  id?: string;
  organizationId: string;
  oktoMachineId: string;
  type: PlanningMachineEventType;
  startAt: number;
  endAt: number;
  durationMinutes: number;
  comment: string;
}

/** Frozen operation unfrozen with another one: of its block, or a later step */
export type PlanningUnfrozenDependent = {
  id: string;
  /** Of the same block (machine, job, material and format), else a later step */
  sameBlock: boolean;
  jobNumber: number | null;
  productName: string;
  operation: string;
  machine: string;
  /** Planned start (ms), null when unknown */
  projectedStart: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class PlanningService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Subscribe to the operations planned to end after their workshop deadline
   * @param organizationId Current organization id
   * @returns Late operations subscription query
   */
  listLate(organizationId: string): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('deadlineExceeded', '==', true),
      where('archived', '==', false)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the late order alerts judged unjustified
   * @param organizationId Current organization id
   * @returns Acknowledgements subscription query
   */
  listLateAcks(organizationId: string): Query<PlanningLateAck> {
    return query(collection(this.firestore, 'planning-late-acks'),
      where('organizationId', '==', organizationId)).withConverter(PlanningLateAck.converter);
  }

  /**
   * Judge the late alert of an order unjustified until its lateness conditions change
   * @param organizationId Current organization id
   * @param jobNumber Order job number
   * @param signature Lateness conditions at acknowledgement time
   * @param userId Acknowledging user id
   */
  async acknowledgeLate(organizationId: string, jobNumber: number, signature: string, userId: string): Promise<void> {
    const ack = new PlanningLateAck({
      organizationId, jobNumber, signature, acknowledgedBy: userId, acknowledgedAt: new Date(),
    });
    await setDoc(doc(this.firestore, 'planning-late-acks', `${organizationId}_${jobNumber}`)
      .withConverter(PlanningLateAck.converter), ack);
  }

  /**
   * Put an acknowledged late alert back in the main list
   * @param organizationId Current organization id
   * @param jobNumber Order job number
   */
  async restoreLate(organizationId: string, jobNumber: number): Promise<void> {
    await deleteDoc(doc(this.firestore, 'planning-late-acks', `${organizationId}_${jobNumber}`));
  }

  /**
   * Subscribe to schedulable operations of a machine that end inside the agenda window
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @param start First instant included (projected end)
   * @param end Last instant included (projected end)
   * @returns Planned operations subscription query
   */
  listByMachine(organizationId: string, machineId: number, start: Date, end: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('archived', '==', false),
      where('projectedEnd', '>=', start),
      where('projectedEnd', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Operations of a machine without a slot: the orders the auto planning does not plan (not validated yet, or
   * delivered after the days it plans), which the dashboard counts from their duration
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @returns Operations without a projected end
   */
  listUnplacedByMachine(organizationId: string, machineId: number): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('archived', '==', false),
      where('projectedEnd', '==', null)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the poses with a date inside a window, for the pose agenda. A pose is where its operation puts it:
   * the start of its plan. Archived poses are read too, so the past stays in sight. Poses of a window start before it
   * when they last several days: the caller widens the window by what a pose can last.
   * @param organizationId Current organization id
   * @param start First instant included (start of the plan)
   * @param end Last instant included (start of the plan)
   * @returns Dated poses query
   */
  listDatedPoses(organizationId: string, start: Date, end: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('installationPlan.startAt', '>=', start),
      where('installationPlan.startAt', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the poses started inside a window, for the pose agenda: a pose is where it was punched once started
   * @param organizationId Current organization id
   * @param start First instant included (actual start)
   * @param end Last instant included (actual start)
   * @returns Started poses query
   */
  listStartedPoses(organizationId: string, start: Date, end: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('actualStart', '>=', start),
      where('actualStart', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the poses finished inside a window, for the pose agenda: a pose started long before the period but
   * finished in it is on screen on the days between, and its start is out of the window of the started poses
   * @param organizationId Current organization id
   * @param start First instant included (actual end)
   * @param end Last instant included (actual end)
   * @returns Finished poses query
   */
  listFinishedPoses(organizationId: string, start: Date, end: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('actualEnd', '>=', start),
      where('actualEnd', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the poses nobody dated yet, whatever their delivery: the list next to the pose agenda, which is not
   * paginated
   * @param organizationId Current organization id
   * @returns Poses to plan query
   */
  listPosesToPlan(organizationId: string): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('archived', '==', false),
      where('installationPlan', '==', null)).withConverter(PlanningOperation.converter);
  }

  /**
   * Poses of a job, archived ones included
   * @param organizationId Current organization id
   * @param jobNumber Multipress job number
   * @returns Poses of the job
   */
  async searchPosesByJob(organizationId: string, jobNumber: number):
    Promise<PlanningOperation[]> {
    const snapshot = await getDocs(query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('jobNumber', '==', jobNumber)).withConverter(PlanningOperation.converter));
    return snapshot.docs.map(document => document.data());
  }

  /**
   * Poses of some customers, archived ones included. Firestore refuses a query whose `in` filters multiply to more than
   * 30 disjunctions: the customers go by batches that keep their count times the one of the resources at 30 at most.
   * @param organizationId Current organization id
   * @param customerIds Customer ids, any number
   * @returns Poses of those customers
   */
  async searchPosesByCustomers(organizationId: string, customerIds: string[]):
    Promise<PlanningOperation[]> {
    const batchSize = 30;
    const found = new Map<string, PlanningOperation>();
    for (let index = 0; index < customerIds.length; index += batchSize) {
      const snapshot = await getDocs(query(collection(this.firestore, 'planning-operations'),
        where('organizationId', '==', organizationId),
        where('isPose', '==', true),
        where('customerId', 'in', customerIds.slice(index, index + batchSize))).withConverter(
        PlanningOperation.converter));
      snapshot.docs.forEach(document => found.set(document.id, document.data()));
    }
    return [...found.values()];
  }

  /**
   * Subscribe to the poses of a Multipress installation resource, whatever their dates: the poseurs of that
   * resource can take them
   * @param organizationId Current organization id
   * @param sourceMachineId Multipress id of the installation resource
   * @returns Poses query
   */
  listBySourceMachine(organizationId: string, sourceMachineId: number): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('sourceMachineId', '==', sourceMachineId),
      where('archived', '==', false)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the poses assigned to an installation resource, whatever their machine
   * @param organizationId Current organization id
   * @param resourceId Installation resource id
   * @returns Poses query
   */
  listAssignedTo(organizationId: string, resourceId: string): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('isPose', '==', true),
      where('archived', '==', false),
      where('installationResourceIds', 'array-contains', resourceId)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the unfinished operations of a machine planned before the agenda window: the ones in the window
   * are already loaded, and an unfinished operation earlier in the punch queue blocks the ones after it even when
   * it is not on screen
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @param before Start of the agenda window (projected end excluded)
   * @returns Unfinished operations query
   */
  listUnfinishedByMachine(organizationId: string, machineId: number, before: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('archived', '==', false),
      where('actualEnd', '==', null),
      where('projectedEnd', '<', before)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to the unfinished operations assigned to an installation resource and planned before the agenda
   * window (see listUnfinishedByMachine)
   * @param organizationId Current organization id
   * @param resourceId Installation resource id
   * @param before Start of the agenda window (projected end excluded)
   * @returns Unfinished operations query
   */
  listUnfinishedByInstallationResource(organizationId: string, resourceId: string, before: Date):
    Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('installationResourceIds', 'array-contains', resourceId),
      where('archived', '==', false),
      where('actualEnd', '==', null),
      where('projectedEnd', '<', before)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to schedulable operations punched on a machine during the agenda window
   * (a tile is drawn on its start day, so bounding actualStart covers every visible punched tile;
   * work still running is also returned by the projectedEnd query)
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @param start First instant included (actual start)
   * @param end Last instant included (actual start)
   * @returns Punched operations subscription query
   */
  listPunchedByMachine(organizationId: string, machineId: number, start: Date, end: Date): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('archived', '==', false),
      where('actualStart', '>=', start),
      where('actualStart', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to schedulable operations assigned to an installation resource that end in the window
   * @param organizationId Current organization id
   * @param resourceId Installation resource id
   * @param start First instant included (projected end)
   * @param end Last instant included (projected end)
   * @returns Assigned operations query
   */
  listByInstallationResource(
    organizationId: string,
    resourceId: string,
    start: Date,
    end: Date
  ): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('installationResourceIds', 'array-contains', resourceId),
      where('archived', '==', false),
      where('projectedEnd', '>=', start),
      where('projectedEnd', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to punched operations assigned to an installation resource during the window
   * (a tile is drawn on its start day, so bounding actualStart covers every visible punched tile;
   * work still running is also returned by the projectedEnd query)
   * @param organizationId Current organization id
   * @param resourceId Installation resource id
   * @param start First instant included (actual start)
   * @param end Last instant included (actual start)
   * @returns Punched operations query
   */
  listPunchedByInstallationResource(
    organizationId: string,
    resourceId: string,
    start: Date,
    end: Date
  ): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('installationResourceIds', 'array-contains', resourceId),
      where('archived', '==', false),
      where('actualStart', '>=', start),
      where('actualStart', '<=', end)).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to synchronized capacities of a machine over a period
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @param startDate Period start (YYYY-MM-DD)
   * @param endDate Period end (YYYY-MM-DD)
   * @returns Planning capacities subscription query
   */
  listCapacitiesByMachine(
    organizationId: string,
    machineId: number,
    startDate: string,
    endDate: string
  ): Query<PlanningCapacity> {
    return query(collection(this.firestore, 'planning-capacities'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)).withConverter(PlanningCapacity.converter);
  }

  /**
   * Subscribe to capacities of one installation resource
   * @param organizationId Current organization id
   * @param resourceId Installation resource id
   * @param startDate First date
   * @param endDate Last date
   * @returns Installation resource capacities query
   */
  listCapacitiesByInstallationResource(
    organizationId: string,
    resourceId: string,
    startDate: string,
    endDate: string
  ): Query<PlanningCapacity> {
    return query(collection(this.firestore, 'planning-capacities'),
      where('organizationId', '==', organizationId),
      where('installationResourceId', '==', resourceId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)).withConverter(PlanningCapacity.converter);
  }

  /**
   * Subscribe to machine events overlapping a period, including long events that end after it
   * @param organizationId Current organization id
   * @param machineId Multipress machine id
   * @param start Period start (exclusive bound on the event end)
   * @param end Period end (inclusive bound on the event start)
   * @returns Machine event subscription query
   */
  listMachineEvents(
    organizationId: string,
    machineId: number,
    start: Date,
    end: Date
  ): Query<PlanningMachineEvent> {
    return query(collection(this.firestore, 'planning-machine-events'),
      where('organizationId', '==', organizationId),
      where('machineId', '==', machineId),
      where('endAt', '>', start),
      where('startAt', '<=', end)).withConverter(PlanningMachineEvent.converter);
  }

  /**
   * Create a machine event and recalculate planning
   * @param payload Event payload
   * @returns Created event id
   */
  async createMachineEvent(payload: PlanningMachineEventPayload): Promise<string> {
    const result = await httpsCallable<PlanningMachineEventPayload, { id: string }>(
      this.functions, 'create_planning_machine_event_gen2')(payload);
    return result.data.id;
  }

  /**
   * Update a machine event and recalculate planning
   * @param payload Event payload
   * @returns Updated event id
   */
  async updateMachineEvent(payload: PlanningMachineEventPayload): Promise<string> {
    const result = await httpsCallable<PlanningMachineEventPayload, { id: string }>(
      this.functions, 'update_planning_machine_event_gen2')(payload);
    return result.data.id;
  }

  /**
   * Delete a machine event and recalculate planning
   * @param organizationId Current organization id
   * @param id Event id
   * @returns Deleted event id
   */
  async deleteMachineEvent(organizationId: string, id: string): Promise<string> {
    const result = await httpsCallable<{ organizationId: string; id: string }, { id: string }>(
      this.functions, 'delete_planning_machine_event_gen2')({ organizationId, id });
    return result.data.id;
  }

  /**
   * Archive the planning operations of an order: they are flagged, never deleted, and leave the planning
   * @param organizationId Current organization id
   * @param orderId Firestore order id
   * @returns Written and deleted counts
   */
  async archiveOrderOperations(organizationId: string, orderId: string): Promise<{
    operations: number;
    anomalies: number;
  }> {
    const result = await httpsCallable<
      { organizationId: string; orderId: string },
      { operations: number; anomalies: number }
    >(this.functions, 'archive_planning_order_operations_gen2')({ organizationId, orderId });
    return result.data;
  }

  /**
   * Restore the planning operations of an order: those not punched yet are planned again from scratch, their
   * delays cleared; those already punched keep their punches
   * @param organizationId Current organization id
   * @param orderId Firestore order id
   * @returns Written count
   */
  async restoreOrderOperations(organizationId: string, orderId: string): Promise<{ operations: number }> {
    const result = await httpsCallable<
      { organizationId: string; orderId: string },
      { operations: number }
    >(this.functions, 'restore_planning_order_operations_gen2')({ organizationId, orderId });
    return result.data;
  }

  /**
   * Remap Multipress operations and refresh the delivery date of one job
   * @param organizationId Current organization id
   * @param jobNumber Multipress job number
   * @param replan Whether the planning is recalculated before answering, false to leave it to the next scheduled run
   * @returns Refresh result
   */
  async refreshOrderFromMultipress(organizationId: string, jobNumber: string, replan = true): Promise<{
    orderId: string;
    jobNumber: string;
    deliveryDate: string;
  }> {
    const result = await httpsCallable<
      { organizationId: string; jobNumber: string; replan: boolean },
      { orderId: string; jobNumber: string; deliveryDate: string }
    >(this.functions, 'refresh_planning_order_gen2')({ organizationId, jobNumber, replan });
    return result.data;
  }

  /**
   * Document held by the backend while it recalculates the planning of an organization
   * @param organizationId Organization id
   * @returns Lock document reference, whose `releasedAt` changes when a recalculation ends
   */
  recalculationLock(organizationId: string): DocumentReference {
    return doc(this.firestore, 'planning-locks', organizationId);
  }

  /**
   * Record or update the real end of a planned operation; the backend derives the start
   * @param organizationId Current organization id
   * @param id Operation id
   * @param actualEnd Real end chosen by the operator
   * @returns Updated operation id
   */
  async punchOperation(organizationId: string, id: string, actualEnd: Date): Promise<string> {
    await httpsCallable<{ organizationId: string; id: string; actualEnd: number }, { id: string }>(
      this.functions, 'punch_planning_operation_gen2')({
      organizationId,
      id,
      actualEnd: actualEnd.getTime(),
    });
    return id;
  }

  /**
   * Record or update the real end of a changeover or maintenance; the backend derives the start
   * @param organizationId Current organization id
   * @param id Event id
   * @param actualEnd Real end chosen by the operator
   * @returns Updated event id
   */
  async punchMachineEvent(organizationId: string, id: string, actualEnd: Date): Promise<string> {
    await httpsCallable<{ organizationId: string; id: string; actualEnd: number }, { id: string }>(
      this.functions, 'punch_planning_machine_event_gen2')({
      organizationId,
      id,
      actualEnd: actualEnd.getTime(),
    });
    return id;
  }

  /**
   * Frozen operations that unfreezing an operation would unfreeze as well: the rest of its block, then later steps
   * @param organizationId Current organization id
   * @param id Operation id
   * @returns Frozen operations, its block first, then by step
   */
  async unfrozenDependents(organizationId: string, id: string): Promise<PlanningUnfrozenDependent[]> {
    const result = await httpsCallable<{ organizationId: string; id: string; preview: boolean },
      { id: string; dependents: PlanningUnfrozenDependent[] }>(
      this.functions, 'unfreeze_planning_operation_gen2')({ organizationId, id, preview: true });
    return result.data.dependents;
  }

  /**
   * Unfreeze a planned operation, the rest of its block and its frozen later steps: the next recalculation plans them
   * again like the others
   * @param organizationId Current organization id
   * @param id Operation id
   * @returns Number of operations unfrozen, the operation included
   */
  async unfreezeOperation(organizationId: string, id: string): Promise<number> {
    const result = await httpsCallable<{ organizationId: string; id: string },
      { id: string; dependents: PlanningUnfrozenDependent[] }>(
      this.functions, 'unfreeze_planning_operation_gen2')({ organizationId, id });
    return result.data.dependents.length + 1;
  }

  /**
   * Assign installation resources to an operation and recalculate planning
   * @param organizationId Current organization id
   * @param operationId Planning operation id
   * @param installationResourceIds Selected resources
   * @returns Updated operation id
   */
  async updateInstallationResources(
    organizationId: string,
    operationId: string,
    installationResourceIds: string[]
  ): Promise<string> {
    const callable = httpsCallable<{
      organizationId: string;
      operationId: string;
      installationResourceIds: string[];
      mode: 'manual';
    }, { id: string }>(this.functions, 'update_planning_operation_resources_gen2');
    const result = await callable({
      organizationId, operationId, installationResourceIds, mode: 'manual',
    });
    return result.data.id;
  }

  /**
   * Fix a pose at a date for a duration on its poseurs, or give it back to the automatic planning, and recalculate
   * planning
   * @param organizationId Current organization id
   * @param operationId Planning operation id
   * @param plan Start, duration in hours, imperative flag and poseurs; null to give the pose back to the planning
   * @returns Updated operation id
   */
  async updateInstallationPlan(
    organizationId: string,
    operationId: string,
    plan: {
      startAt: Date;
      durationHours: number;
      imperative: boolean;
      installationResourceIds: string[];
      /** Pieces of a pose cut by hand: the duration is then their total and the start the first one */
      parts?: { startAt: Date; durationHours: number }[];
      /** Title and comment of a pose with no job */
      title?: string;
      comment?: string;
    } | null
  ): Promise<string> {
    const callable = httpsCallable<{
      organizationId: string;
      operationId: string;
      startAt?: number;
      durationHours?: number;
      imperative?: boolean;
      installationResourceIds?: string[];
      parts?: { startAt: number; durationHours: number }[];
      title?: string;
      comment?: string;
      clear?: boolean;
    }, { id: string }>(this.functions, 'update_planning_installation_plan_gen2');
    const result = await callable(plan ? {
      organizationId,
      operationId,
      startAt: plan.startAt.getTime(),
      durationHours: plan.durationHours,
      imperative: plan.imperative,
      installationResourceIds: plan.installationResourceIds,
      ...(plan.title === undefined ? {} : { title: plan.title, comment: plan.comment ?? '' }),
      ...(plan.parts && plan.parts.length > 1 ? {
        parts: plan.parts.map(part => ({ startAt: part.startAt.getTime(), durationHours: part.durationHours })),
      } : {}),
    } : { organizationId, operationId, clear: true });
    return result.data.id;
  }

  /**
   * Create a pose with no job: a title, a comment, a date, a duration (or pieces) and poseurs
   * @param organizationId Current organization id
   * @param pose Pose to create
   * @returns Id of the pose created
   */
  async createInstallationPose(organizationId: string, pose: {
    title: string;
    comment: string;
    startAt: Date;
    durationHours: number;
    installationResourceIds: string[];
    parts?: { startAt: Date; durationHours: number }[];
  }): Promise<string> {
    const callable = httpsCallable<Record<string, unknown>, { id: string }>(
      this.functions, 'create_installation_pose_gen2');
    const result = await callable({
      organizationId,
      title: pose.title,
      comment: pose.comment,
      startAt: pose.startAt.getTime(),
      durationHours: pose.durationHours,
      installationResourceIds: pose.installationResourceIds,
      ...(pose.parts && pose.parts.length > 1 ? {
        parts: pose.parts.map(part => ({ startAt: part.startAt.getTime(), durationHours: part.durationHours })),
      } : {}),
    });
    return result.data.id;
  }

  /**
   * Delete a pose with no job that is not started
   * @param organizationId Current organization id
   * @param operationId Pose id
   * @returns Id of the pose deleted
   */
  async deleteInstallationPose(organizationId: string, operationId: string): Promise<string> {
    const callable = httpsCallable<{ organizationId: string; operationId: string }, { id: string }>(
      this.functions, 'delete_installation_pose_gen2');
    const result = await callable({ organizationId, operationId });
    return result.data.id;
  }

  /**
   * Set the poseurs of a pose without touching its date: it keeps the date set by hand, or the planning dates it
   * the day the order is delivered
   * @param organizationId Current organization id
   * @param operationId Planning operation id (the pose)
   * @param installationResourceIds Poseurs of the pose, none to give it back to the automatic assignment
   * @returns Updated operation id
   */
  async assignInstallationResources(
    organizationId: string,
    operationId: string,
    installationResourceIds: string[]
  ): Promise<string> {
    const callable = httpsCallable<{
      organizationId: string;
      operationId: string;
      installationResourceIds: string[];
    }, { id: string }>(this.functions, 'update_planning_installation_plan_gen2');
    const result = await callable({ organizationId, operationId, installationResourceIds });
    return result.data.id;
  }

  /**
   * Manually set which order product(s) a planning operation covers (first link or correction)
   * @param organizationId Current organization id
   * @param operationId Planning operation id
   * @param poIds Selected order product-line ids
   * @returns Number of product lines the operation was split into, and whether the matching
   *   link_failed anomaly was resolved (best-effort: a link can succeed while this is false)
   */
  async linkOperationToProducts(
    organizationId: string,
    operationId: string,
    poIds: string[]
  ): Promise<{ count: number; anomalyResolved: boolean }> {
    const callable = httpsCallable<{
      organizationId: string;
      id: string;
      poIds: string[];
    }, { count: number; anomalyResolved: boolean }>(this.functions, 'link_planning_operation_to_products_gen2');
    const result = await callable({ organizationId, id: operationId, poIds });
    return result.data;
  }

  /**
   * Reference of one planned operation, to follow it while a dialog shows it
   * @param id Operation id
   * @returns Operation document reference
   */
  operationRef(id: string): DocumentReference<PlanningOperation> {
    return doc(this.firestore, 'planning-operations', id).withConverter(PlanningOperation.converter);
  }

  /**
   * Subscribe to every operation planned for a Multipress job
   * @param organizationId Current organization id
   * @param jobNumber Multipress job number
   * @returns Planned operations subscription query
   */
  listByJob(organizationId: string, jobNumber: number): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('jobNumber', '==', jobNumber)).withConverter(PlanningOperation.converter);
  }

  /**
   * Load every operation planned for a Multipress job
   * @param organizationId Current organization id
   * @param jobNumber Multipress job number
   * @returns Planned operations of the job
   */
  async getByJob(organizationId: string, jobNumber: number): Promise<PlanningOperation[]> {
    const snapshot = await getDocs(this.listByJob(organizationId, Number(jobNumber)));
    return snapshot.docs.map(document => document.data());
  }

  /**
   * Load every operation planned for an Okto order
   * @param organizationId Current organization id
   * @param orderId Firestore order id
   * @returns Planned operations of the order
   */
  async getByOrder(organizationId: string, orderId: string): Promise<PlanningOperation[]> {
    if (!organizationId || !orderId) {
      return [];
    }
    const snapshot = await getDocs(this.listByOrder(organizationId, orderId));
    return snapshot.docs.map(document => document.data());
  }

  /**
   * Subscribe to every operation planned for an Okto order
   * @param organizationId Current organization id
   * @param orderId Firestore order id
   * @returns Planned operations subscription query
   */
  listByOrder(organizationId: string, orderId: string): Query<PlanningOperation> {
    return query(collection(this.firestore, 'planning-operations'),
      where('organizationId', '==', organizationId),
      where('orderId', '==', orderId)).withConverter(PlanningOperation.converter);
  }
}
